import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { Spinner } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter } from '@creditornot/cb-modal';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { fetchNpsSurvey, fetchTranslationResource, submitNpsSurvey } from 'apis/merchant-gateway/nps/api';
import { MatrixAnswer, OpenEndedAnswer, SurveyResponse } from 'apis/merchant-gateway/nps/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';

import { SurveyLocaleContext } from './survey-monkey/SurveyLocaleContext';
import SurveyMonkeyQuestionnaire, { SubmittableSurveyForm } from './survey-monkey/SurveyMonkeyQuestionnaire';

type SurveyModalContentProps = {
  onSubmissionSuccess: () => void;
  onSubmissionError: () => void;
  venue?: Venue;
};

const ActionsFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ErrorAlert = styled(Alert).attrs({ variant: 'error' })`
  margin-block-start: 14px;
`;

const SurveyModalContent = ({ onSubmissionSuccess, onSubmissionError, venue }: SurveyModalContentProps) => {
  const formRef = useRef<SubmittableSurveyForm>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const intl = useIntl();

  const { data = null, isLoading } = useQuery(['/merchant-analytics-api/surveys/nps'], () => fetchNpsSurvey(), {
    refetchOnWindowFocus: false,
  });

  const validate = (values: SurveyResponse) => {
    const {
      pages: [page],
    } = values;
    const {
      questions: [question],
    } = page;
    let isValid = true;
    if (question.family === 'matrix') {
      const {
        answers: [answer],
      } = question;
      isValid = (answer as MatrixAnswer).choiceId !== '' && (answer as MatrixAnswer).choiceId != null;
    }

    if (!isValid) {
      setValidationError(intl.formatMessage({ id: 'nps.validation-error' }));
      setTimeout(() => {
        setValidationError(null);
      }, 2500);
    }

    return isValid;
  };

  const { data: translation = { id: 'en', translations: [] }, isLoading: isTranslationLoading } = useQuery(
    ['/merchant-analytics-api/surveys/nps/translations/', intl.locale],
    () => fetchTranslationResource({ locale: intl.locale }),
    {
      refetchOnWindowFocus: false,
      enabled: intl.locale !== 'en',
      retry: false,
    },
  );

  const submitSurveyMutation = useMutation(
    (values: SurveyResponse) =>
      submitNpsSurvey({
        merchantId: null,
        venueId: venue ? venue.id : null,
        surveyResponse: values,
      }),
    {
      onSuccess: () => onSubmissionSuccess(),
      onError: () => onSubmissionError(),
    },
  );

  const submitForm = (values: SurveyResponse) => {
    if (validate(values)) {
      const submissionValues: SurveyResponse = {
        ...values,
        pages: values.pages.map(page => ({
          ...page,
          questions: page.questions
            .filter(question => {
              if (question.family === 'open_ended') {
                return (question.answers[0] as OpenEndedAnswer).text.trim() !== '';
              }
              return true;
            })
            .map(question => ({ id: question.id, answers: question.answers })),
        })),
      };

      submitSurveyMutation.mutate(submissionValues);
    }
  };

  const isSurveyLoading = isLoading || (intl.locale !== 'en' && isTranslationLoading);

  return (
    <>
      <ModalContent>
        {!isSurveyLoading && !!data && (
          <SurveyLocaleContext.Provider value={translation}>
            <SurveyMonkeyQuestionnaire survey={data} onSubmit={submitForm} ref={formRef} />
          </SurveyLocaleContext.Provider>
        )}
        {isSurveyLoading && <Spinner size="small" />}
        {validationError && <ErrorAlert>{validationError}</ErrorAlert>}
      </ModalContent>
      <ActionsFooter>
        <Button
          size="small"
          variant="blue"
          onClick={() => formRef.current?.submitForm()}
          loading={submitSurveyMutation.isLoading}
          disabled={submitSurveyMutation.isLoading}
        >
          <FormattedMessage id="nps.questionnaire.submit" />
        </Button>
      </ActionsFooter>
    </>
  );
};

export default SurveyModalContent;
